// src/pages/AffiliateTagsSection.tsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Tag, 
  Plus, 
  Edit2, 
  Trash2, 
  Save, 
  X, 
  Star,
  RefreshCw,
  Eye,
  Users,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService, AffiliateTagPool } from '../services/api';
import type { AffiliateTag, TagUsersResponse } from '../services/api';

// Rotasyon havuzları — ekranda bu sırayla, ayrı bölümler halinde gösterilir.
// Rotasyon yalnız aynı havuzun tagları arasında döner (bkz. TagRotationService).
const POOL_GROUPS: { pool: AffiliateTagPool; title: string; hint: string; accent: string; chip: string }[] = [
  { pool: 'general', title: 'Genel', hint: 'Normal kullanıcıların rotasyonu',
    accent: 'border-purple-200', chip: 'bg-purple-50 text-purple-700 border-purple-200' },
  { pool: 'bringgoship', title: 'BringGo', hint: 'Yalnız bringgoship hesabının rotasyonu',
    accent: 'border-emerald-200', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { pool: 'autoorder', title: 'AutoOrder', hint: 'eBay auto-order eklentisinin rotasyonu',
    accent: 'border-blue-200', chip: 'bg-blue-50 text-blue-700 border-blue-200' },
];

export default function AffiliateTagsSection() {
  const [tags, setTags] = useState<AffiliateTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTag, setEditingTag] = useState<AffiliateTag | null>(null);
  const [formData, setFormData] = useState<{ tagCode: string; description: string; isActive: boolean; pool: AffiliateTagPool }>({ tagCode: '', description: '', isActive: true, pool: 'general' });
  const [saving, setSaving] = useState(false);

  // Kullanıcı listesi modal state'leri
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [tagUsers, setTagUsers] = useState<TagUsersResponse | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Gruplar: kapalı havuz bölümleri ve açılmış "pasif" alt listeleri
  const [collapsedPools, setCollapsedPools] = useState<Record<string, boolean>>({});
  const [openPassive, setOpenPassive] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    setLoading(true);
    try {
      const response = await apiService.getAffiliateTags();
      setTags(response.tags);
    } catch (error: any) {
      toast.error(error.message || 'Taglar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const loadTagUsers = async (tagId: string) => {
    setLoadingUsers(true);
    setShowUsersModal(true);
    try {
      const response = await apiService.getTagUsers(tagId);
      setTagUsers(response);
    } catch (error: any) {
      toast.error(error.message || 'Kullanıcılar yüklenemedi');
      setShowUsersModal(false);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.tagCode.trim()) {
      toast.error('Tag kodu gerekli');
      return;
    }

    setSaving(true);
    try {
      if (editingTag) {
  await apiService.updateAffiliateTag(editingTag.id, {
    tagCode: formData.tagCode.trim(),
    description: formData.description.trim() || undefined,
    isActive: formData.isActive,
    pool: formData.pool,
    isDefault: editingTag.isDefault
  });
  toast.success('Tag güncellendi');
      } else {
        await apiService.createAffiliateTag({
          tagCode: formData.tagCode.trim(),
          description: formData.description.trim() || undefined,
          isActive: formData.isActive,
          pool: formData.pool
        });
        toast.success('Tag oluşturuldu');
      }
      resetForm();
      loadTags();
    } catch (error: any) {
      toast.error(error.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (tag: AffiliateTag) => {
    setEditingTag(tag);
    setFormData({
      tagCode: tag.tagCode,
      description: tag.description || '',
      isActive: tag.isActive,
      pool: tag.pool || 'general'
    });
    setShowForm(true);
  };

  const handleDelete = async (tag: AffiliateTag) => {
    if (tag.isDefault) {
      toast.error('Varsayılan tag silinemez');
      return;
    }
    
    if (!window.confirm(`"${tag.tagCode}" tagını silmek istediğinize emin misiniz?`)) {
      return;
    }

    try {
      await apiService.deleteAffiliateTag(tag.id);
      toast.success('Tag silindi');
      loadTags();
    } catch (error: any) {
      toast.error(error.message || 'Silme başarısız');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingTag(null);
    setFormData({ tagCode: '', description: '', isActive: true, pool: 'general' });
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Yeni Tag Ekle
          </button>
        </div>
        <button
          onClick={loadTags}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Yenile
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-lg border border-purple-200"
        >
          <h3 className="text-lg font-bold text-gray-800 mb-4">
            {editingTag ? 'Tag Düzenle' : 'Yeni Tag Ekle'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tag Kodu *
                </label>
                <input
                  type="text"
                  value={formData.tagCode}
                  onChange={(e) => setFormData({ ...formData, tagCode: e.target.value })}
                  placeholder="örn: mystore-20"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Açıklama
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Tag açıklaması (opsiyonel)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rotasyon Havuzu</label>
              <select
                value={formData.pool}
                onChange={(e) => setFormData({ ...formData, pool: e.target.value as AffiliateTagPool })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="general">Genel — normal kullanıcılar</option>
                <option value="bringgoship">BringGo — sadece bringgoship hesabı</option>
                <option value="autoorder">AutoOrder — eBay auto-order eklentisi</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Rotasyon yalnızca aynı havuzdaki taglar arasında döner. Bir havuzda hiç tag yoksa
                o trafik <strong>etiketsiz</strong> geçer — başka havuzun tagı kullanılmaz.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="w-4 h-4 text-purple-600 rounded"
              />
              <label htmlFor="isActive" className="text-sm text-gray-700">Aktif</label>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {editingTag ? 'Güncelle' : 'Kaydet'}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                İptal
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Tags — havuza göre gruplu */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      ) : tags.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <Tag className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Henüz tag eklenmemiş</p>
        </div>
      ) : (
        <div className="space-y-5">
          {POOL_GROUPS.map((g) => {
            const inPool = tags.filter((t) => (t.pool || 'general') === g.pool);
            // Varsayılan tag en üstte, sonra alfabetik
            const byCode = (a: AffiliateTag, b: AffiliateTag) =>
              (a.isDefault === b.isDefault ? 0 : a.isDefault ? -1 : 1) || a.tagCode.localeCompare(b.tagCode);
            const active = inPool.filter((t) => t.isActive).sort(byCode);
            const passive = inPool.filter((t) => !t.isActive).sort(byCode);
            const collapsed = !!collapsedPools[g.pool];
            const passiveOpen = !!openPassive[g.pool];

            const renderRow = (tag: AffiliateTag) => (
              <tr key={tag.id} className={`hover:bg-gray-50 ${tag.isActive ? '' : 'opacity-70'}`}>
                <td className="px-6 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-purple-700 bg-purple-50 px-2 py-1 rounded text-sm font-medium">
                      {tag.tagCode}
                    </span>
                    {tag.isDefault && (
                      <span title="Varsayılan tag" className="inline-flex items-center gap-1 text-xs text-yellow-700">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        Varsayılan
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-3 text-gray-600 text-sm truncate" title={tag.description || ''}>{tag.description || '—'}</td>
                <td className="px-6 py-3">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-sm">
                    <Users className="w-3 h-3" />
                    {tag.userCount}
                  </span>
                </td>
                <td className="px-6 py-3">
                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                    tag.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {tag.isActive ? '✓ Aktif' : '✗ Pasif'}
                  </span>
                </td>
                <td className="px-6 py-3">
                  <div className="flex items-center justify-center gap-2">
                    <button onClick={() => loadTagUsers(tag.id)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" title="Kullanıcıları Gör">
                      <Eye className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleEdit(tag)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg" title="Düzenle">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {!tag.isDefault && (
                      <button onClick={() => handleDelete(tag)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Sil">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );

            return (
              <div key={g.pool} className={`bg-white rounded-xl shadow-lg overflow-hidden border ${g.accent}`}>
                {/* Havuz başlığı */}
                <button
                  type="button"
                  onClick={() => setCollapsedPools((prev) => ({ ...prev, [g.pool]: !prev[g.pool] }))}
                  className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left hover:bg-gray-50"
                  aria-expanded={!collapsed}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {collapsed ? <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />}
                    <span className={`px-2.5 py-1 rounded-full text-sm font-semibold border ${g.chip}`}>{g.title}</span>
                    <span className="text-sm text-gray-500 truncate">{g.hint}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 text-xs">
                    <span className="px-2 py-1 rounded bg-green-50 text-green-700">{active.length} aktif</span>
                    {passive.length > 0 && <span className="px-2 py-1 rounded bg-gray-100 text-gray-600">{passive.length} pasif</span>}
                  </div>
                </button>

                {!collapsed && (
                  inPool.length === 0 ? (
                    <div className="px-6 py-5 border-t text-sm text-gray-500">
                      Bu havuzda tag yok — bu havuzun trafiği <strong>etiketsiz</strong> geçer.
                    </div>
                  ) : (
                    <div className="border-t overflow-x-auto">
                      {/* Sabit kolon genişlikleri: bölümler arasında kolonlar hizalı kalsın */}
                      <table className="w-full table-fixed min-w-[760px]">
                        <colgroup>
                          <col className="w-[28%]" /><col className="w-[32%]" /><col className="w-[12%]" /><col className="w-[12%]" /><col className="w-[16%]" />
                        </colgroup>
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Tag Kodu</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Açıklama</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Kullanıcı</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Durum</th>
                            <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">İşlemler</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {active.length === 0 && (
                            <tr><td colSpan={5} className="px-6 py-4 text-sm text-amber-700 bg-amber-50">
                              Bu havuzda aktif tag yok — trafiği <strong>etiketsiz</strong> geçer.
                            </td></tr>
                          )}
                          {active.map(renderRow)}
                          {passive.length > 0 && (
                            <tr className="bg-gray-50">
                              <td colSpan={5} className="px-6 py-2">
                                <button
                                  type="button"
                                  onClick={() => setOpenPassive((prev) => ({ ...prev, [g.pool]: !prev[g.pool] }))}
                                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
                                  aria-expanded={passiveOpen}
                                >
                                  {passiveOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                  Pasif taglar ({passive.length})
                                </button>
                              </td>
                            </tr>
                          )}
                          {passiveOpen && passive.map(renderRow)}
                        </tbody>
                      </table>
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Kullanıcılar Modal */}
      {showUsersModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden flex flex-col"
          >
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Tag className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">
                      {tagUsers?.tagCode || 'Tag'} Kullanıcıları
                    </h3>
                    <p className="text-sm text-gray-500">
                      {tagUsers?.userCount || 0} kullanıcı
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowUsersModal(false); setTagUsers(null); }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingUsers ? (
                <div className="py-12 text-center">
                  <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600">Yükleniyor...</p>
                </div>
              ) : tagUsers?.users.length === 0 ? (
                <div className="py-12 text-center">
                  <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Bu tag'a atanmış kullanıcı yok</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {tagUsers?.users.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                          {user.fullName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-800">{user.fullName}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        user.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {user.isActive ? 'Aktif' : 'Pasif'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => { setShowUsersModal(false); setTagUsers(null); }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Kapat
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}